import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Public Pages
import LandingPage from "./pages/LandingPage";
import PricingPage from "./pages/PricingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import InstallPage from "./pages/InstallPage";
import AboutPage from "./pages/AboutPage";
import MCPDocsPage from "./pages/MCPDocsPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import HelpPage from "./pages/HelpPage";
import AuthCallback from "./pages/AuthCallback";

// Dashboard
import DashboardLayout from "./pages/DashboardLayout";
import DashboardOverview from "./pages/dashboard/DashboardOverview";
import UsagePage from "./pages/dashboard/UsagePage";
import SubscriptionPage from "./pages/dashboard/SubscriptionPage";
import ExtensionPage from "./pages/dashboard/ExtensionPage";
import MCPKeysPage from "./pages/dashboard/MCPKeysPage";
import SupportPage from "./pages/dashboard/SupportPage";
import BrainPage from "./pages/dashboard/BrainPage";
import TestimonialsPage from "./pages/dashboard/TestimonialsPage";
import SettingsPage from "./pages/dashboard/SettingsPage";
import ProgressPage from "./pages/dashboard/ProgressPage";
import AchievementsPage from "./pages/dashboard/AchievementsPage";
import ReferralPage from "./pages/dashboard/ReferralPage";
import TokenOptimizerPage from "./pages/dashboard/TokenOptimizerPage";
import PICastPage from "./pages/dashboard/PICastPage";
import PILoopPage from "./pages/dashboard/PILoopPage";
import MyCastsPage from "./pages/dashboard/MyCastsPage";
import PIStorePage from "./pages/dashboard/PIStorePage";
import PICheckPage from "./pages/dashboard/PICheckPage";
import PICraftPage from "./pages/dashboard/PICraftPage";

// Feedback
import DoNotSellPage from "./pages/DoNotSellPage";
import FeedbackPage from "./pages/FeedbackPage";

// Admin
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminLayout from "./pages/AdminLayout";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import AdminTestimonials from "./pages/admin/AdminTestimonials";
import AdminSupport from "./pages/admin/AdminSupport";
import AdminPilotHealth from "./pages/admin/AdminPilotHealth";
import AdminChat from "./pages/admin/AdminChat";
import AdminUsage from "./pages/admin/AdminUsage";
import AdminFeedback from "./pages/admin/AdminFeedback";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminBroadcast from "./pages/admin/AdminBroadcast";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminTraffic from "./pages/admin/AdminTraffic";
import AdminTeam from "./pages/admin/AdminTeam";
import AdminKnowledgeBase from "./pages/admin/AdminKnowledgeBase";
import AdminTemplates from "./pages/admin/AdminTemplates";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminImpersonate from "./pages/admin/AdminImpersonate";
import AdminHealthDashboard from "./pages/admin/AdminHealthDashboard";
import AdminPrivacy from "./pages/admin/AdminPrivacy";
import AdminFeatures from "./pages/admin/AdminFeatures";

// Support
import SupportLoginPage from "./pages/SupportLoginPage";
import SupportLayout from "./pages/SupportLayout";
import SupportDashboard from "./pages/support/SupportDashboard";
import SupportTickets from "./pages/support/SupportTickets";
import SupportTicketDetail from "./pages/support/SupportTicketDetail";
import SupportChatPage from "./pages/support/SupportChatPage";

import { CookieConsentBanner } from "./components/CookieConsentBanner";
import { SignupNudge } from "./components/SignupNudge";
import { initSessionTracking, trackPageChange } from "./utils/sessionTracker";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();

  // Initialize session tracking on mount, track route changes
  useEffect(() => { initSessionTracking(); }, []);
  useEffect(() => { trackPageChange(location.pathname); }, [location.pathname]);
  // Check URL fragment synchronously for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/install" element={<InstallPage />} />
          <Route path="/about" element={<AboutPage />} />
          {/* MCP docs hidden for pilot — re-enable when ready */}
          {/* <Route path="/docs/mcp" element={<MCPDocsPage />} /> */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/do-not-sell" element={<DoNotSellPage />} />
          <Route path="/help" element={<HelpPage />} />

          {/* Dashboard (User Tier) */}
          <Route
            path="/dashboard"
            element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}
          >
            <Route index element={<DashboardOverview />} />
            <Route path="usage" element={<UsagePage />} />
            <Route path="subscription" element={<SubscriptionPage />} />
            <Route path="extension" element={<ExtensionPage />} />
            <Route path="mcp" element={<MCPKeysPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="brain" element={<BrainPage />} />
            <Route path="progress" element={<ProgressPage />} />
            <Route path="achievements" element={<AchievementsPage />} />
            <Route path="referrals" element={<ReferralPage />} />
            <Route path="testimonial" element={<TestimonialsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="optimizer" element={<TokenOptimizerPage />} />
            <Route path="cast" element={<PICastPage />} />
            <Route path="loop" element={<PILoopPage />} />
            <Route path="my-casts" element={<MyCastsPage />} />
            <Route path="store" element={<PIStorePage />} />
            <Route path="check" element={<PICheckPage />} />
            <Route path="craft" element={<PICraftPage />} />
          </Route>

          {/* Feedback (User Tier) */}
          <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />

          {/* Admin (Tier 2) */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:userId" element={<AdminUserDetail />} />
            <Route path="usage" element={<AdminUsage />} />
            <Route path="pilot" element={<AdminPilotHealth />} />
            <Route path="chat" element={<AdminChat />} />
            <Route path="feedback" element={<AdminFeedback />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="messages" element={<AdminBroadcast />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="traffic" element={<AdminTraffic />} />
            <Route path="team" element={<AdminTeam />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="knowledge-base" element={<AdminKnowledgeBase />} />
            <Route path="templates" element={<AdminTemplates />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="impersonate/:userId" element={<AdminImpersonate />} />
            <Route path="health" element={<AdminHealthDashboard />} />
            <Route path="privacy" element={<AdminPrivacy />} />
            <Route path="features" element={<AdminFeatures />} />
          </Route>

          {/* Support (Tier 3) */}
          <Route path="/support/login" element={<SupportLoginPage />} />
          <Route path="/support" element={<SupportLayout />}>
            <Route index element={<SupportDashboard />} />
            <Route path="tickets" element={<SupportTickets />} />
            <Route path="tickets/:ticketId" element={<SupportTicketDetail />} />
            <Route path="chat/:chatId" element={<SupportChatPage />} />
          </Route>
        </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <ErrorBoundary>
      <BrowserRouter>
        <AppRouter />
        <CookieConsentBanner />
        <SignupNudge />
      </BrowserRouter>
      </ErrorBoundary>
      <Toaster position="top-right" />
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
